import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { EyeIcon } from "@phosphor-icons/react";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { MemoIsland } from "../../MemoIsland/MemoIsland";
import { MemoOptionsMenu } from "../../ProjectHeader/_MemoOptionsMenu";
import { ValueChainIsland } from "../../ValueChainIsland/ValueChainIsland";
import { CaseFrameIsland } from "../../FrameIsland/CaseFrameIsland";
import { ProjectSetupIsland } from "../../FrameIsland/ProjectSetupIsland";
import type { TSectionPage } from "../model/types";
import { SECTION_TITLE_STICKY_TOP, SectionPage, sectionStickyTop } from "./SectionPage";
import { ChapterTools, ChapterWidget } from "./ChapterWidget";
import { WidgetCard, WidgetCardHeader } from "./WidgetCard";

export function ChapterSectionScreen({
  page,
  toolsDisabled,
  toolPanel
}: {
  page: TSectionPage;
  toolsDisabled: boolean;
  toolPanel: ReactNode;
}) {
  const { activeChapter } = useChapterStore();
  const { selectedProject } = useProjectStore();
  const { isLabs } = useMeStore();
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";
  const contentFormat = activeChapter?.activeMemo?.contentFormat;
  const isEditor = contentFormat !== "blocks" && contentFormat !== "html";
  const hasTabs = page.tabs.length > 0;
  const chapterName = activeChapter?.projectTemplateChapter.name;
  const tools = (
    <ChapterTools
      disabled={toolsDisabled}
      menu={!isTemplateLess && isLabs ? <MemoOptionsMenu disabled={toolsDisabled} /> : undefined}
    />
  );

  if (isEditor) {
    return (
      <SectionPage page={page} fill>
        <ChapterWidget
          header={{ title: page.widgetTitle, actions: tools }}
          stickyTop={sectionStickyTop(hasTabs)}
          fill
          toolPanel={toolPanel}
        >
          <MemoIsland inWidget showTitle={page.widgetTitle !== chapterName} />
        </ChapterWidget>
      </SectionPage>
    );
  }

  const nameAboveCard = hasTabs ? page.tabs.find((tab) => tab.id === page.activeTabId)?.label : page.title;
  return (
    <SectionPage page={page} actions={tools}>
      <ChapterWidget stickyTop={hasTabs ? sectionStickyTop(true) : SECTION_TITLE_STICKY_TOP} toolPanel={toolPanel}>
        <MemoIsland inWidget showTitle={nameAboveCard !== chapterName} />
      </ChapterWidget>
    </SectionPage>
  );
}

export function ValueChainSectionScreen({ page }: { page: TSectionPage }) {
  const { t } = useTranslation("projectHome");

  return (
    <SectionPage page={page} fill>
      <WidgetCard className="min-h-0 flex-1">
        <WidgetCardHeader
          title={page.widgetTitle}
          titleAdornment={
            <span className="typo-caption flex shrink-0 items-center gap-1 rounded-full border border-foreground/10 px-2 py-0.5 text-muted-foreground">
              <EyeIcon className="size-3.5" weight="regular" />
              {t("valueChain.readOnly")}
            </span>
          }
        />
        <div className="min-h-0 flex-1 overflow-hidden rounded-b-2xl">
          <ValueChainIsland inWidget />
        </div>
      </WidgetCard>
    </SectionPage>
  );
}

export function FrameSectionScreen({ page }: { page: TSectionPage }) {
  const { viewMode } = useChapterStore();
  const isCaseFrame = viewMode === "projectFrame";
  return (
    <SectionPage page={page} fill={isCaseFrame}>
      {isCaseFrame ? <CaseFrameIsland /> : <ProjectSetupIsland />}
    </SectionPage>
  );
}

export function SectionStateScreen({ page, children }: { page: TSectionPage; children: ReactNode }) {
  return (
    <SectionPage page={page} fill>
      <WidgetCard className="min-h-0 flex-1">
        <div className="flex min-h-[60svh] flex-1 flex-col">{children}</div>
      </WidgetCard>
    </SectionPage>
  );
}
